import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import SEO from '../components/SEO';
import { getGlossaryTerm, GLOSSARY_TERMS } from '../data/glossaryTerms';

const CATEGORY_COLORS = {
  'Basics':     '#38bdf8',
  'Investing':  '#a78bfa',
  'Retirement': '#22c55e',
  'Debt':       '#ef4444',
  'Savings':    '#f59e0b',
  'Markets':    '#22c55e',
  'Taxes':      '#f472b6',
  'Credit':     '#fb923c',
};

export default function GlossaryTerm() {
  const { slug } = useParams();
  const term = getGlossaryTerm(slug);

  if (!term) {
    return (
      <div style={s.page}>
        <div style={s.inner}>
          <h1 style={s.h1}>Term not found</h1>
          <Link to="/glossary" style={s.back}>← Back to glossary</Link>
        </div>
      </div>
    );
  }

  const color = CATEGORY_COLORS[term.category] || '#64748b';

  // Related terms data
  const relatedData = (term.relatedTerms || [])
    .map(slug => GLOSSARY_TERMS.find(t => t.slug === slug))
    .filter(Boolean)
    .slice(0, 4);

  // Structured data for this term
  const termSchema = {
    '@context': 'https://schema.org',
    '@type': 'DefinedTerm',
    name: term.term,
    description: term.fullDef,
    inDefinedTermSet: {
      '@type': 'DefinedTermSet',
      name: 'KnowFi Financial Glossary',
      url: 'https://knowfi.io/glossary',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [{
      '@type': 'Question',
      name: `What is ${term.term}?`,
      acceptedAnswer: { '@type': 'Answer', text: term.fullDef },
    }],
  };

  return (
    <div style={s.page}>
      <SEO
        title={`${term.term} — Definition & Plain English Explanation | KnowFi`}
        description={`${term.shortDef} Full explanation of ${term.term} in plain English — no jargon.`}
        path={`/glossary/${term.slug}`}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(termSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div style={s.glow} />
      <div style={s.inner}>

        {/* Breadcrumb */}
        <div style={s.breadcrumb}>
          <Link to="/glossary" style={s.breadLink}>Glossary</Link>
          <span style={s.breadSep}>›</span>
          <span style={s.breadCurrent}>{term.term}</span>
        </div>

        {/* Header */}
        <div style={s.header}>
          <div style={s.metaRow}>
            <span style={{ ...s.catBadge, background: color + '18', color, border: `1px solid ${color}33` }}>
              {term.category}
            </span>
            <span style={s.letterBadge}>Letter {term.letter}</span>
          </div>
          <h1 style={s.h1}>{term.term}</h1>
          <p style={s.shortDef}>{term.shortDef}</p>
        </div>

        {/* Full definition */}
        <div style={s.defBox}>
          <div style={s.defLabel}>📖 Full Definition</div>
          <p style={s.fullDef}>{term.fullDef}</p>
        </div>

        {/* KnowFi feature CTA */}
        {term.knowfiFeature && (
          <Link to={term.knowfiFeature.to} style={s.featureCard}>
            <span style={s.featureIcon}>💡</span>
            <div>
              <div style={s.featureLabel}>Put it into practice</div>
              <div style={s.featureLink}>{term.knowfiFeature.label} →</div>
            </div>
          </Link>
        )}

        {/* Related terms */}
        {relatedData.length > 0 && (
          <div style={s.relatedSection}>
            <div style={s.relatedTitle}>Related Terms</div>
            <div style={s.relatedGrid}>
              {relatedData.map(rt => {
                const rColor = CATEGORY_COLORS[rt.category] || '#64748b';
                return (
                  <Link key={rt.slug} to={`/glossary/${rt.slug}`} style={s.relatedCard}>
                    <span style={{ ...s.relatedCat, color: rColor }}>{rt.category}</span>
                    <div style={s.relatedTerm}>{rt.term}</div>
                    <div style={s.relatedShort}>{rt.shortDef}</div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* Disclaimer */}
        <div style={s.disclaimer}>
          <strong>Disclaimer:</strong> This definition is for educational purposes only and is not financial advice. Always consult a qualified financial professional before making financial decisions.
        </div>

        {/* Back */}
        <div style={s.backRow}>
          <Link to="/glossary" style={s.back}>← Back to full glossary</Link>
          <Link to="/blog" style={s.blogsLink}>Read our guides →</Link>
        </div>

      </div>
    </div>
  );
}

const s = {
  page:  { background: '#0f172a', color: '#f8fafc', minHeight: '100vh', padding: '40px 24px 80px', position: 'relative', overflow: 'hidden' },
  glow:  { position: 'absolute', top: '-60px', left: '50%', transform: 'translateX(-50%)', width: '600px', height: '400px', background: 'radial-gradient(ellipse at center, rgba(167,139,250,0.07) 0%, transparent 65%)', pointerEvents: 'none' },
  inner: { maxWidth: '760px', margin: '0 auto', position: 'relative', zIndex: 1 },

  breadcrumb: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '32px' },
  breadLink:  { color: '#475569', textDecoration: 'none', fontSize: '13px' },
  breadSep:   { color: '#334155', fontSize: '13px' },
  breadCurrent:{ color: '#64748b', fontSize: '13px' },

  header:   { marginBottom: '28px' },
  metaRow:  { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' },
  catBadge: { fontSize: '11px', fontWeight: '700', padding: '3px 10px', borderRadius: '20px', textTransform: 'uppercase', letterSpacing: '0.3px' },
  letterBadge: { fontSize: '11px', color: '#475569', background: '#1e293b', padding: '3px 10px', borderRadius: '20px', border: '1px solid #334155' },
  h1:       { fontSize: 'clamp(24px, 4vw, 38px)', fontWeight: '900', margin: '0 0 12px', letterSpacing: '-0.5px', lineHeight: 1.2, color: '#f1f5f9' },
  shortDef: { fontSize: '18px', color: '#64748b', margin: 0, lineHeight: 1.55, fontStyle: 'italic', borderLeft: '3px solid #334155', paddingLeft: '14px' },

  defBox:   { background: '#1e293b', borderRadius: '14px', padding: '24px 28px', marginBottom: '24px', border: '1px solid #334155' },
  defLabel: { fontSize: '12px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' },
  fullDef:  { fontSize: '16px', color: '#94a3b8', lineHeight: 1.75, margin: 0 },

  featureCard:  { display: 'flex', alignItems: 'center', gap: '14px', background: 'rgba(167,139,250,0.06)', border: '1px solid rgba(167,139,250,0.2)', borderRadius: '12px', padding: '16px 20px', marginBottom: '32px', textDecoration: 'none', color: '#f8fafc' },
  featureIcon:  { fontSize: '24px', flexShrink: 0 },
  featureLabel: { fontSize: '12px', color: '#64748b', marginBottom: '4px' },
  featureLink:  { fontSize: '14px', fontWeight: '700', color: '#a78bfa' },

  relatedSection: { marginBottom: '32px' },
  relatedTitle:   { fontSize: '14px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '14px' },
  relatedGrid:    { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '10px' },
  relatedCard:    { background: '#1e293b', borderRadius: '10px', padding: '14px 16px', border: '1px solid #334155', textDecoration: 'none', color: '#f8fafc', display: 'flex', flexDirection: 'column', gap: '6px' },
  relatedCat:     { fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.3px' },
  relatedTerm:    { fontSize: '13px', fontWeight: '700', color: '#f1f5f9' },
  relatedShort:   { fontSize: '12px', color: '#475569', lineHeight: 1.45 },

  disclaimer: { padding: '12px 16px', background: 'rgba(100,116,139,0.06)', border: '1px solid #1e293b', borderRadius: '8px', fontSize: '12px', color: '#475569', lineHeight: 1.6, marginBottom: '24px' },

  backRow:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '16px', borderTop: '1px solid #1e293b' },
  back:       { color: '#475569', textDecoration: 'none', fontSize: '13px' },
  blogsLink:  { color: '#38bdf8', textDecoration: 'none', fontSize: '13px', fontWeight: '600' },
};
